import type { CSSProperties } from 'react'
import {
  computeDebris,
  computeDust,
  DUST_COUNT,
  IDLE_MOTES,
  IDLE_SPARKS,
} from '../../revealSequence'

/** Everything the settled exact card loops: ambient dust plus the two layers of
 * the idle burst. Clamped globally by particleBudget.ts, because two exact
 * scores in one gameweek is normal and each runs the full set. */
export const IDLE_PARTICLE_COST = DUST_COUNT + IDLE_SPARKS + IDLE_MOTES

// Fixed points for the four-point star glints. Three is deliberate: at five
// they stopped reading as an occasional catch of light and started reading as
// decoration.
const GLINTS = [
  { left: '26%', top: '30%' },
  { left: '78%', top: '68%' },
  { left: '54%', top: '22%' },
]

function debrisStyle(piece: ReturnType<typeof computeDebris>[number]): CSSProperties {
  return {
    '--tx': `${piece.tx}px`,
    '--ty': `${piece.ty}px`,
    '--g': `${piece.gravity}px`,
    '--ps': `${piece.size}px`,
    '--pd': `${piece.delay}ms`,
    '--pdur': `${piece.duration}ms`,
    '--rot': `${piece.angle}deg`,
  } as CSSProperties
}

/**
 * The exact-score card's surface and light (ticket 0418) — the aurora and grain
 * that make it read as a material, the specular pass, and the rim light
 * travelling its border with the bloom it drags.
 *
 * Shared by the settled card and the live one, which is the point: a live exact
 * score moves exactly like the banked one. What it does NOT get is the
 * celebration (see `ExactRestingParticles`) and the locked ring — it keeps the
 * app's thin pulsing one, so the card still says "not yet".
 *
 * Order matters. The z-index ladder lives in GameCard.css: surface layers paint
 * under the card's own copy, glass layers over it.
 */
export function ExactSurfaceLayers({ ring }: { ring: 'locked' | 'live' }) {
  return (
    <>
      <span className="game-card__rim game-card__rim--glow" aria-hidden="true" />
      <span className="game-card__aurora" aria-hidden="true">
        <span className="game-card__aurora-blob game-card__aurora-blob--1" />
        <span className="game-card__aurora-blob game-card__aurora-blob--2" />
        <span className="game-card__aurora-blob game-card__aurora-blob--3" />
      </span>
      <span className="game-card__grain" aria-hidden="true" />
      {/* The specular pass has to be clipped to the card edge, so it keeps its
          own wrapper — the card itself is not overflow:hidden. */}
      <span className="game-card__shine-wrap" aria-hidden="true">
        <span className="game-card__shine" />
        <span className="game-card__shine game-card__shine--trail" />
      </span>
      {ring === 'locked' ? (
        <span className="game-card__exact-ring" aria-hidden="true" />
      ) : (
        <span className="game-card__live-ring" aria-hidden="true" />
      )}
      <span className="game-card__rim" aria-hidden="true" />
    </>
  )
}

/**
 * The settled card's celebration: glints, drifting dust and the looping idle
 * burst. Reserved for full time — a live exact score has won nothing yet.
 *
 * `share` is the fraction of the requested particles the global budget granted;
 * a partial grant thins every layer evenly rather than dropping one whole.
 */
export function ExactRestingParticles({ share }: { share: number }) {
  if (share <= 0) return null
  const dust = computeDust(Math.round(DUST_COUNT * share))
  const motes = computeDebris(Math.round(IDLE_MOTES * share), 'mote', 'idle')
  const sparks = computeDebris(Math.round(IDLE_SPARKS * share), 'spark', 'idle')

  return (
    <>
      {GLINTS.map((spot, i) => (
        <span
          key={`glint-${i}`}
          className="game-card__glint"
          style={{ ...spot, '--gi': i } as CSSProperties}
          aria-hidden="true"
        />
      ))}
      {dust.map((speck, i) => (
        <span
          key={`dust-${i}`}
          className={`game-card__dust game-card__dust--c${speck.colour}`}
          style={
            {
              left: `${speck.x}%`,
              top: `${speck.y}%`,
              '--ps': `${speck.size}px`,
              '--pa': speck.opacity,
              '--tx': `${speck.tx}px`,
              '--ty': `${speck.ty}px`,
              '--pdur': `${speck.duration}ms`,
              '--pd': `${speck.delay}ms`,
            } as CSSProperties
          }
          aria-hidden="true"
        />
      ))}
      {motes.map((piece, i) => (
        <span
          key={`idle-mote-${i}`}
          className={`game-card__idle-mote game-card__dust--c${piece.colour}`}
          style={debrisStyle(piece)}
          aria-hidden="true"
        />
      ))}
      {sparks.map((piece, i) => (
        <span
          key={`idle-spark-${i}`}
          className={`game-card__idle-spark game-card__dust--c${piece.colour}`}
          style={debrisStyle(piece)}
          aria-hidden="true"
        />
      ))}
    </>
  )
}
